import { Container, Graphics } from 'pixi.js';

import { WORLD_TILE_HEIGHT, WORLD_TILE_WIDTH } from '../../game/config';
import type { Season, SeasonState, WeatherType } from '../seasons/SeasonTypes';
import type { MapData } from '../world/types';

const SEASON_TINT: Record<Season, { color: number; alpha: number }> = {
  spring: { color: 0x8fd66d, alpha: 0.035 },
  summer: { color: 0xffc65c, alpha: 0.028 },
  autumn: { color: 0xd77a2e, alpha: 0.075 },
  winter: { color: 0xeaf7ff, alpha: 0.018 },
};

/**
 * Adds readable seasonal changes to both tile and baked voxel worlds.
 * The surface layer stays below entities; airborne particles stay above them.
 */
export class SeasonEffectsRenderer {
  private readonly surface = new Graphics();
  private readonly atmosphere = new Graphics();
  private surfaceSignature = '';

  constructor(surfaceLayer: Container, atmosphereLayer: Container) {
    this.surface.label = 'Jahreszeiten-Boden';
    this.atmosphere.label = 'Jahreszeiten-Atmosphäre';
    this.surface.eventMode = 'none';
    this.atmosphere.eventMode = 'none';
    surfaceLayer.addChild(this.surface);
    atmosphereLayer.addChild(this.atmosphere);
  }

  render(map: MapData, season: SeasonState, elapsedMs = performance.now()): void {
    const width = map.width * WORLD_TILE_WIDTH;
    const height = map.height * WORLD_TILE_HEIGHT;
    const winterCoverage = season.currentSeason === 'winter'
      ? Math.round(Math.max(0.72, season.snowCoverage) * 10) / 10
      : 0;
    const signature = `${map.width}:${map.height}:${season.currentSeason}:${winterCoverage}`;
    if (signature !== this.surfaceSignature) {
      this.surfaceSignature = signature;
      this.drawSurface(width, height, season.currentSeason);
    }
    this.drawAtmosphere(width, height, season, elapsedMs / 1000);
  }

  private drawSurface(width: number, height: number, season: Season): void {
    this.surface.clear();
    const tint = SEASON_TINT[season];
    this.surface.rect(0, 0, width, height).fill({ color: tint.color, alpha: tint.alpha });

    if (season === 'spring') {
      this.drawGroundSpecks(width, height, 58, [0xffd2e8, 0xfff3a8, 0xd8f5ff], 3, 0.6);
      return;
    }
    if (season === 'summer') {
      this.drawGroundSpecks(width, height, 34, [0xffe28a, 0xe9ca59], 4, 0.38);
      return;
    }
    if (season === 'autumn') {
      this.drawGroundSpecks(width, height, 82, [0xc94d28, 0xe47b26, 0xf2b43c, 0x8b3f21], 6, 0.72);
      return;
    }

    // Winter ground and roof snow are authored directly into dedicated voxel
    // textures. This layer only adds a nearly invisible cold light so colors,
    // water, roads and material contrast remain intact.
  }

  private drawGroundSpecks(
    width: number,
    height: number,
    count: number,
    colors: readonly number[],
    size: number,
    alpha: number,
  ): void {
    for (let index = 0; index < count; index += 1) {
      const x = this.seed(index, 23) * width;
      const y = this.seed(index, 61) * height;
      const color = colors[index % colors.length];
      const scale = 0.65 + this.seed(index, 89) * 0.7;
      this.surface
        .rect(Math.round(x), Math.round(y), Math.max(2, size * scale), Math.max(2, size * 0.62 * scale))
        .fill({ color, alpha });
    }
  }

  private drawAtmosphere(width: number, height: number, season: SeasonState, time: number): void {
    this.atmosphere.clear();
    this.drawSeasonParticles(width, height, season.currentSeason, time);
    this.drawWeather(width, height, season.currentWeather, time);
  }

  private drawSeasonParticles(width: number, height: number, season: Season, time: number): void {
    if (season === 'spring') {
      this.drawFallingParticles(width, height, time, 18, [0xffc9df, 0xfff1f6], 0.34, 12, 5);
    } else if (season === 'autumn') {
      this.drawFallingParticles(width, height, time, 26, [0xd45b27, 0xef9734, 0x9d3e24], 0.62, 18, 8);
    } else if (season === 'winter') {
      this.drawFallingParticles(width, height, time, 44, [0xffffff, 0xdff3ff], 0.72, 24, 7);
    } else {
      for (let index = 0; index < 12; index += 1) {
        const x = this.seed(index, 151) * width;
        const y = this.seed(index, 163) * height;
        const glow = 0.18 + Math.sin(time * 1.3 + index) * 0.08;
        this.atmosphere.circle(x, y, 3 + (index % 3)).fill({ color: 0xffe89b, alpha: glow });
      }
    }
  }

  private drawFallingParticles(
    width: number,
    height: number,
    time: number,
    count: number,
    colors: readonly number[],
    alpha: number,
    speed: number,
    size: number,
  ): void {
    for (let index = 0; index < count; index += 1) {
      const phase = this.seed(index, 191);
      const x = (this.seed(index, 211) * width + Math.sin(time * 0.8 + index) * 26 + width) % width;
      const y = (phase * height + time * speed * (0.7 + this.seed(index, 227))) % height;
      const particleSize = Math.max(3, size * (0.55 + this.seed(index, 239) * 0.55));
      this.atmosphere
        .rect(Math.round(x), Math.round(y), particleSize, Math.max(2, particleSize * 0.55))
        .fill({ color: colors[index % colors.length], alpha });
    }
  }

  private drawWeather(width: number, height: number, weather: WeatherType, time: number): void {
    if (weather === 'cloudy') {
      this.atmosphere.rect(0, 0, width, height).fill({ color: 0x6f8290, alpha: 0.045 });
      return;
    }
    if (weather === 'fog') {
      this.atmosphere.rect(0, 0, width, height).fill({ color: 0xe8f0ee, alpha: 0.08 });
      for (let index = 0; index < 6; index += 1) {
        const y = ((index + 0.5) / 6) * height;
        const drift = Math.sin(time * 0.13 + index) * width * 0.025;
        this.atmosphere
          .roundRect(drift - width * 0.05, y, width * 1.1, 44, 22)
          .fill({ color: 0xf3f7f4, alpha: 0.055 });
      }
      return;
    }
    if (weather === 'rain' || weather === 'storm') {
      this.atmosphere.rect(0, 0, width, height).fill({
        color: weather === 'storm' ? 0x27364c : 0x607c91,
        alpha: weather === 'storm' ? 0.09 : 0.04,
      });
      for (let index = 0; index < (weather === 'storm' ? 58 : 38); index += 1) {
        const x = (this.seed(index, 263) * width + time * 24) % width;
        const y = (this.seed(index, 277) * height + time * 170) % height;
        this.atmosphere
          .moveTo(x, y)
          .lineTo(x - 8, y + 22)
          .stroke({ color: 0xa9dcf3, width: 2, alpha: 0.42 });
      }
      if (weather === 'storm' && Math.sin(time * 0.75) > 0.985) {
        this.atmosphere.rect(0, 0, width, height).fill({ color: 0xdbe9ff, alpha: 0.14 });
      }
      return;
    }
    if (weather === 'wind') {
      for (let index = 0; index < 12; index += 1) {
        const x = (this.seed(index, 293) * width + time * 86) % width;
        const y = this.seed(index, 307) * height;
        this.atmosphere
          .moveTo(x, y)
          .bezierCurveTo(x + 18, y - 8, x + 42, y + 8, x + 66, y)
          .stroke({ color: 0xf1eee0, width: 3, alpha: 0.25 });
      }
      return;
    }
    if (weather === 'frost') {
      this.atmosphere.rect(0, 0, width, height).fill({ color: 0xd8f2ff, alpha: 0.055 });
    }
  }

  private seed(index: number, salt: number): number {
    const value = Math.sin((index + 1) * 12.9898 + salt * 78.233) * 43758.5453;
    return value - Math.floor(value);
  }
}
